import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronRight, ChevronLeft, ChevronsLeft } from "lucide-react";
import { Card } from "../../components/Card";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { api } from "../../lib/api";
import type { AdminTenantSummary } from "../../lib/me";

const PAGE_SIZE = 15;

interface PaginatedTenants {
  data: AdminTenantSummary[];
  pagination: { nextCursor: string | null; hasMore: boolean };
}

export default function AdminTenants() {
  const [data, setData] = useState<PaginatedTenants | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [page, setPage] = useState(0);

  const fetchPage = async (cursor: string | null, q: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      if (cursor) params.set("cursor", cursor);
      if (q) params.set("q", q);
      const result = await api.getRaw<PaginatedTenants>(`/api/admin/tenants?${params}`);
      setData(result);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCursors([null]);
    setPage(0);
    fetchPage(null, query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const goNext = async () => {
    if (!data?.pagination.nextCursor) return;
    const cs = [...cursors, data.pagination.nextCursor];
    setCursors(cs);
    setPage(page + 1);
    await fetchPage(data.pagination.nextCursor, query);
  };
  const goPrev = async () => {
    if (page === 0) return;
    const cs = cursors.slice(0, -1);
    setCursors(cs);
    setPage(page - 1);
    await fetchPage(cs[cs.length - 1] ?? null, query);
  };
  const goFirst = async () => {
    setCursors([null]);
    setPage(0);
    await fetchPage(null, query);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Tenants</h1>
          <p className="mt-1 text-sm text-gray-500">All onboarded tenants. Click into one to manage plan, quota, or status.</p>
        </div>
        <div className="w-72">
          <Input
            placeholder="Search by slug or company name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      {!data || (loading && data.data.length === 0) ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : data.data.length === 0 ? (
        <Card className="p-8 text-center text-sm text-gray-500">
          No tenants {query ? `matching "${query}"` : "yet"}.
        </Card>
      ) : (
        <>
          <Card>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  <th className="px-5 py-3">Tenant</th>
                  <th className="px-5 py-3">Owner</th>
                  <th className="px-5 py-3">Plan</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Quota</th>
                  <th className="px-5 py-3">Trial expires</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((t) => (
                  <tr key={t.tenantId} className="border-b border-gray-50 last:border-0 hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <Link to={`/admin/tenants/${t.tenantId}`} className="block">
                        <div className="font-medium text-gray-900">{t.displayName}</div>
                        <div className="text-xs text-gray-500">{t.tenantId} · support@{t.inboundSubdomain}</div>
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-700">
                      {t.ownerEmail ? (
                        <a href={`mailto:${t.ownerEmail}`} className="hover:underline">{t.ownerEmail}</a>
                      ) : (
                        <span className="text-gray-400 italic">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <PlanPill plan={t.plan} />
                    </td>
                    <td className="px-5 py-3">
                      <StatusPill status={t.status} />
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-gray-700">
                      {t.quotaEmailsUsed}/{t.quotaEmailsLimit}
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-600">
                      {t.trialExpiresAt ? new Date(t.trialExpiresAt).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Link to={`/admin/tenants/${t.tenantId}`} className="text-gray-400 hover:text-gray-900">
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Page {page + 1} · {data.data.length} tenants</p>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={goFirst} disabled={page === 0 || loading}>
                <ChevronsLeft className="h-3.5 w-3.5" /> First
              </Button>
              <Button size="sm" variant="secondary" onClick={goPrev} disabled={page === 0 || loading}>
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </Button>
              <Button size="sm" onClick={goNext} disabled={!data.pagination.hasMore || loading}>
                Next <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PlanPill({ plan }: { plan: string }) {
  const cls = plan === "paid"
    ? "bg-emerald-100 text-emerald-700"
    : plan === "internal"
    ? "bg-purple-100 text-purple-700"
    : "bg-gray-100 text-gray-700";
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{plan}</span>;
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    trial_active: "bg-blue-100 text-blue-700",
    trial_expired: "bg-amber-100 text-amber-700",
    active: "bg-emerald-100 text-emerald-700",
    suspended: "bg-rose-100 text-rose-700",
    provisioning: "bg-gray-100 text-gray-700",
  };
  const cls = map[status] ?? "bg-gray-100 text-gray-700";
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{status}</span>;
}

void Search;
